/* New report: start from a layout (built-in starter layouts + the team's
 * saved templates) or "Build from my data" (pick a dataset, get three
 * rules-based dashboard proposals built from its real rows, create a draft). */
import { useMutation, useQuery } from "@tanstack/react-query";
import { ArrowLeft, Check, CheckSquare, Database, FileSpreadsheet, LayoutTemplate, Link2, PencilRuler, Sparkles, Square, Table2 } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";

import { adminApi } from "../../api/admin";
import { ownerApi } from "../../api/owner";
import { ApiError, apiRequest } from "../../api/client";
import { dataHubApi } from "../../api/dataHub";
import { datasetApi } from "../../api/dataSources";
import { suggestApi, summarizeDefinition, templatesApi, type Proposal } from "../../api/v21";
import { KindLegend, LayoutThumb, describeVisuals } from "../../components/LayoutThumb";
import { Badge, Banner, Button, EmptyState, Field, Input, Modal, SearchInput, Segmented, Select, Skeleton, Textarea, useToast } from "../../ds";
import type { ReportCategory } from "../../types";
import { DesignScoreBadge } from "../../studio/DesignScore";
import { groupDatasets } from "../../studio/LeftPanels";
import { suggestLinks, type LinkField } from "../../studio/modelLinks";
import { TEMPLATES, buildDefinition } from "./templates";

type Path = "layout" | "data";

export function NewReportDialog({ open, onClose, onCreated, initialTemplate, initialPath, mode = "admin", categories, initialCategoryId }: { open: boolean; onClose: () => void; onCreated: (id: string) => void; initialTemplate?: string | null; initialPath?: Path; mode?: "admin" | "owner"; /** Admin only: the categories to offer (MOP, MOS, ...). */ categories?: ReportCategory[]; /** Pre-selected category (the gallery chip that was active). */ initialCategoryId?: string | null }) {
  const api = mode === "owner" ? ownerApi : adminApi;
  const toast = useToast();
  const [path, setPath] = useState<Path>(initialPath ?? "layout");
  const [templateKey, setTemplateKey] = useState("builtin:executive");
  const [datasetId, setDatasetId] = useState<string | null>(null);
  // v2.2: several tables in one report -- picked[0] gets the suggested
  // dashboard, every other table its own page; linked by matching columns.
  const [picked, setPicked] = useState<string[]>([]);
  const [proposalId, setProposalId] = useState<string>("executive");
  const [name, setName] = useState("");
  const [nameTouched, setNameTouched] = useState(false);
  const [description, setDescription] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [dsQuery, setDsQuery] = useState("");

  useEffect(() => {
    if (!open) return;
    setName("");
    setNameTouched(false);
    setDescription("");
    setCategoryId(initialCategoryId && (categories ?? []).some((c) => c.id === initialCategoryId) ? initialCategoryId : "");
    setError(null);
    setDatasetId(null);
    setPicked([]);
    setProposalId("executive");
    setDsQuery("");
    setDsGroup("");
    setPath(initialPath ?? (initialTemplate ? "layout" : "layout"));
    setTemplateKey(initialTemplate ?? "builtin:executive");
    // (categories / initialCategoryId are read when the dialog opens only)
  }, [open, initialTemplate, initialPath]);

  const custom = useQuery({ queryKey: ["templates"], queryFn: templatesApi.list, enabled: open, staleTime: 30_000 });
  const datasets = useQuery({ queryKey: [mode, "datasets"], queryFn: mode === "owner" ? datasetApi.listMine : datasetApi.list, enabled: open && path === "data" });
  const [dsGroup, setDsGroup] = useState<string>("");
  const suggest = useQuery({ queryKey: ["suggest", datasetId], queryFn: () => suggestApi.proposals(datasetId!), enabled: open && !!datasetId, staleTime: 60_000, retry: false });
  const builtins = useMemo(() => TEMPLATES.map((t) => ({ t, summary: summarizeDefinition(buildDefinition(t, "preview", t.name)) })), []);
  const proposal: Proposal | undefined = suggest.data?.proposals.find((p) => p.id === proposalId) ?? suggest.data?.proposals[0];

  useEffect(() => {
    if (path === "data" && datasetId && !nameTouched) {
      const ds = datasets.data?.find((d) => d.id === datasetId);
      if (ds) setName(`${ds.name} — ${proposal?.name.toLowerCase() ?? "dashboard"}`);
    }
  }, [path, datasetId, proposal, nameTouched, datasets.data]);

  const extraTables = datasetId ? picked.filter((id) => id !== datasetId) : [];
  const tableName = (id: string) => datasets.data?.find((d) => d.id === id)?.name ?? "Table";
  const fieldsBase = mode === "owner" ? "/api/v1/owner/datasets" : "/api/v1/admin/datasets";
  const links = useQuery({
    queryKey: ["new-report-links", mode, picked.join("|")],
    enabled: open && picked.length > 1,
    staleTime: 60_000,
    queryFn: async () => {
      // v2.3: links from real overlapping values (confident ones only); the
      // "same column name" rule is the fallback when the rows can't be read.
      try {
        const r = await dataHubApi.suggestLinks(picked);
        return r.links.filter((l) => l.confidence >= 0.75).map((l) => ({ fromDatasetId: l.fromDatasetId, fromField: l.fromField, toDatasetId: l.toDatasetId, toField: l.toField, why: l.why }));
      } catch {
        const f = Object.fromEntries(await Promise.all(picked.map(async (id) => [id, await apiRequest<LinkField[]>(`${fieldsBase}/${id}/fields`)] as const)));
        return suggestLinks(picked, (id) => f[id] ?? []);
      }
    },
  });

  /** One page per extra table (its own "executive" suggestion), unique ids,
   * plus definition.model with every picked table and the suggested links. */
  const addTablePages = async (definition: Record<string, any>, primary: string, extras: string[]) => {
    const pages = [...(definition.pages ?? [])];
    for (const [i, id] of extras.entries()) {
      try {
        const s = await suggestApi.proposals(id);
        const p = s.proposals.find((x) => x.id === "executive") ?? s.proposals[0];
        if (!p) continue;
        if (p.measuresToCreate.length) await suggestApi.ensureMeasures(id, p.measuresToCreate);
        for (const [j, pg] of (structuredClone(p.definition).pages ?? []).entries()) {
          const pid = `t${i + 1}-${pg.id ?? j}`;
          pages.push({ ...pg, id: pid, name: j === 0 ? tableName(id) : `${tableName(id)} · ${pg.name ?? j + 1}`, widgets: (pg.widgets ?? []).map((w: any) => ({ ...w, id: `t${i + 1}-${w.id}` })) });
        }
      } catch {
        // A table we can't read right now simply doesn't get a page; it's still in the model.
      }
    }
    const found = links.data ?? (await links.refetch()).data ?? [];
    return {
      ...definition,
      pages,
      model: {
        tables: [primary, ...extras],
        relationships: found.map((l, k) => ({ id: `link-${k + 1}`, fromDatasetId: l.fromDatasetId, fromField: l.fromField, toDatasetId: l.toDatasetId, toField: l.toField, crossFilter: "both", active: true })),
      },
    };
  };

  const create = useMutation({
    mutationFn: async () => {
      const reportName = name.trim();
      let definition: Record<string, any>;
      if (path === "data") {
        if (!proposal || !datasetId) throw new Error("Pick one of the suggested layouts first.");
        if (proposal.measuresToCreate.length) await suggestApi.ensureMeasures(datasetId, proposal.measuresToCreate);
        definition = structuredClone(proposal.definition);
        if (extraTables.length) definition = await addTablePages(definition, datasetId, extraTables);
      } else if (templateKey.startsWith("custom:")) {
        const t = await templatesApi.get(templateKey.slice(7));
        definition = structuredClone(t.definition ?? {});
      } else {
        const t = TEMPLATES.find((x) => `builtin:${x.id}` === templateKey) ?? TEMPLATES[0];
        definition = buildDefinition(t, "pending", reportName, description.trim());
      }
      const report = showCategory && categoryId
        ? await adminApi.createReport({ name: reportName, description: description.trim() || undefined, category_id: categoryId })
        : await api.createReport({ name: reportName, description: description.trim() || undefined });
      definition.reportId = report.id;
      definition.name = reportName;
      if (description.trim()) definition.description = description.trim();
      const draft = await api.getDraft(report.id);
      await api.saveDraft(report.id, { definition, expectedRevision: draft.revision_number });
      return report;
    },
    onSuccess: (r) => {
      toast.success("Report created", path === "data" ? "Opening the designer — your visuals are already connected to the data." : "Opening the designer — bind each visual to your data.");
      onClose();
      onCreated(r.id);
    },
    onError: (e) => setError(e instanceof ApiError || e instanceof Error ? e.message : "Could not create the report."),
  });

  const dsGroups = groupDatasets(datasets.data ?? []);
  const dsList = dsGroups
    .filter(([g]) => !dsGroup || g === dsGroup)
    .map(([g, items]) => [g, items
      .filter((d) => !dsQuery || `${d.name} ${d.source_name ?? ""} ${g}`.toLowerCase().includes(dsQuery.toLowerCase()))
      // grouped by file inside a site, so a whole file can be ticked at once
      .sort((a, b) => (a.source_name ?? "").localeCompare(b.source_name ?? "") || a.name.localeCompare(b.name))] as const)
    .filter(([, items]) => items.length > 0);
  const canCreate = !!name.trim() && (path === "layout" || (!!proposal && !suggest.isFetching));
  const profile = suggest.data?.profile;
  const kindsLine = profile
    ? (() => {
        const counts: Record<string, number> = {};
        for (const f of profile.fields) counts[f.kindLabel] = (counts[f.kindLabel] ?? 0) + 1;
        const plural: Record<string, string> = { Category: "categories", "Name / label": "names / labels", ID: "IDs", "Date (as number)": "dates stored as numbers", "Date (as text)": "dates stored as text", "Time period": "time periods", Text: "text columns", Empty: "empty columns" };
        return Object.entries(counts).map(([k, n]) => `${n} ${n > 1 ? plural[k] ?? `${k.toLowerCase()}s` : k === "ID" ? "ID" : k.toLowerCase()}`).join(", ");
      })()
    : "";

  const showCategory = mode === "admin" && (categories ?? []).length > 0;
  const nameFields = (
    <div className="ds-grid" style={{ gridTemplateColumns: showCategory ? "minmax(0, 1.2fr) minmax(0, 1.2fr) minmax(170px, 0.7fr)" : "repeat(2, minmax(0, 1fr))", marginBottom: path === "layout" ? 18 : 0 }}>
      <Field label="Report name" error={error ?? undefined}>
        <Input value={name} onChange={(e) => { setName(e.target.value); setNameTouched(true); }} placeholder="e.g. Fleet utilization — weekly" autoFocus={path === "layout"} onKeyDown={(e) => e.key === "Enter" && canCreate && create.mutate()} />
      </Field>
      <Field label="Description" hint="Optional — shown on the report card.">
        <Textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={2} style={{ minHeight: 36 }} />
      </Field>
      {showCategory ? (
        <Field label="Category" hint="Optional — the group it appears under on the TV Reports menu." htmlFor="new-report-category">
          <Select id="new-report-category" value={categoryId} onChange={(e) => setCategoryId(e.target.value)}>
            <option value="">No category</option>
            {(categories ?? []).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </Select>
        </Field>
      ) : null}
    </div>
  );

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="xl"
      title="New report"
      description={path === "layout" ? "Pick a starting layout. Every visual can be changed later — you're only choosing a head start." : "Pick the data your team updates (for example an Excel sheet in SharePoint). We look at its real rows and suggest three complete dashboards."}
      footer={
        <>
          {path === "data" && datasetId ? <Button variant="ghost" icon={ArrowLeft} onClick={() => setDatasetId(null)} style={{ marginRight: "auto" }}>Choose other data</Button> : null}
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" icon={PencilRuler} loading={create.isPending} disabled={!canCreate} title={!name.trim() ? "Give the report a name first" : undefined} onClick={() => create.mutate()}>
            {path === "data" ? "Create draft & open designer" : "Create & open designer"}
          </Button>
        </>
      }
    >
      <div style={{ marginBottom: 16 }}>
        <Segmented<Path>
          value={path}
          onChange={(v) => { setPath(v); setError(null); }}
          options={[{ value: "layout", label: "Start from a layout", icon: LayoutTemplate }, { value: "data", label: "Build from my data", icon: Sparkles }]}
        />
      </div>

      {path === "layout" ? (
        <>
          {nameFields}
          {(custom.data ?? []).length > 0 ? (
            <>
          <div style={{ fontWeight: 650 }}>Your team's templates</div>
          <div className="ds-small ds-muted" style={{ marginBottom: 8 }}>Layouts people saved from their own reports (<Link to="/templates" onClick={onClose}>manage templates</Link>).</div>
          {custom.isLoading ? (
            <Skeleton height={120} />
          ) : (custom.data ?? []).length === 0 ? (
            <div className="ds-small ds-muted" style={{ padding: "14px 16px", border: "1px dashed var(--border-strong)", borderRadius: 10, marginBottom: 18 }}>
              None yet. Open any report and choose <strong>Save as template</strong> to reuse its layout here.
            </div>
          ) : (
            <div className="ds-grid" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(210px, 1fr))", marginBottom: 18 }}>
              {(custom.data ?? []).map((t) => (
                <TemplateChoice
                  key={t.id}
                  selected={templateKey === `custom:${t.id}`}
                  onSelect={() => setTemplateKey(`custom:${t.id}`)}
                  name={t.name}
                  description={t.description || (t.created_by_name ? `Saved by ${t.created_by_name}` : "")}
                  summary={t.summary}
                  badge={t.keeps_bindings ? <Badge tone="success">Keeps data</Badge> : t.category ? <Badge>{t.category}</Badge> : null}
                />
              ))}
            </div>
          )}
            </>
          ) : null}
          <div className="ds-row" style={{ justifyContent: "space-between", marginBottom: 8, flexWrap: "wrap" }}>
            <div>
              <div style={{ fontWeight: 650 }}>Starter layouts</div>
              <div className="ds-small ds-muted">Shipped with the platform: empty layouts with placeholder visuals — you connect each one to your data in the designer.</div>
            </div>
            <KindLegend />
          </div>
          <div className="ds-grid" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(210px, 1fr))", marginBottom: 18 }}>
            {builtins.map(({ t, summary }) => (
              <TemplateChoice key={t.id} selected={templateKey === `builtin:${t.id}`} onSelect={() => setTemplateKey(`builtin:${t.id}`)} name={t.name} description={t.description} summary={summary} />
            ))}
          </div>
          {!((custom.data ?? []).length > 0) ? (
            <>
          <div style={{ fontWeight: 650 }}>Your team's templates</div>
          <div className="ds-small ds-muted" style={{ marginBottom: 8 }}>Layouts people saved from their own reports (<Link to="/templates" onClick={onClose}>manage templates</Link>).</div>
          {custom.isLoading ? (
            <Skeleton height={120} />
          ) : (custom.data ?? []).length === 0 ? (
            <div className="ds-small ds-muted" style={{ padding: "14px 16px", border: "1px dashed var(--border-strong)", borderRadius: 10, marginBottom: 18 }}>
              None yet. Open any report and choose <strong>Save as template</strong> to reuse its layout here.
            </div>
          ) : (
            <div className="ds-grid" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(210px, 1fr))", marginBottom: 18 }}>
              {(custom.data ?? []).map((t) => (
                <TemplateChoice
                  key={t.id}
                  selected={templateKey === `custom:${t.id}`}
                  onSelect={() => setTemplateKey(`custom:${t.id}`)}
                  name={t.name}
                  description={t.description || (t.created_by_name ? `Saved by ${t.created_by_name}` : "")}
                  summary={t.summary}
                  badge={t.keeps_bindings ? <Badge tone="success">Keeps data</Badge> : t.category ? <Badge>{t.category}</Badge> : null}
                />
              ))}
            </div>
          )}
            </>
          ) : null}
        </>
      ) : !datasetId ? (
        <div>
          <div className="ds-row" style={{ marginBottom: 10 }}>
            <SearchInput value={dsQuery} onChange={setDsQuery} placeholder="Search tables, files or sites" width={340} autoFocus />
            <div className="ds-grow" />
            <span className="ds-small ds-faint">{mode === "admin" ? <>Don't see your sheet? Add it under <Link to="/admin/data" onClick={onClose}>Data</Link>.</> : "Don't see your data? Ask an admin to share its SharePoint site with you."}</span>
          </div>
          {dsGroups.length > 1 ? (
            <div className="ds-row" style={{ flexWrap: "wrap", gap: 6, marginBottom: 10 }} role="group" aria-label="SharePoint site">
              <Button size="sm" variant={dsGroup ? "secondary" : "primary"} onClick={() => setDsGroup("")}>All sites</Button>
              {dsGroups.map(([g, items]) => (
                <Button key={g} size="sm" variant={dsGroup === g ? "primary" : "secondary"} onClick={() => setDsGroup(dsGroup === g ? "" : g)}>{g} <span className="ds-faint" style={{ marginLeft: 4 }}>{items.length}</span></Button>
              ))}
            </div>
          ) : null}
          {datasets.isLoading ? <Skeleton height={220} /> : dsList.length === 0 ? <EmptyState icon={Database} title="No tables match" /> : (
            <div className="ds-list" style={{ maxHeight: 360, overflow: "auto", border: "1px solid var(--border)", borderRadius: 10 }}>
              {dsList.map(([g, items]) => (
                <div key={g}>
                  <div className="ds-list__row ds-small" style={{ fontWeight: 650, background: "var(--surface-2, transparent)", position: "sticky", top: 0, zIndex: 1 }}>
                    <span className="ds-grow">{g}</span>
                    {/* v2.3: a whole site or file in one click, like picking "Finance data" in Power BI. */}
                    {items.length > 1 ? <Button size="sm" variant="ghost" icon={CheckSquare} onClick={() => setPicked((p) => [...new Set([...p, ...items.map((d) => d.id)])])}>Tick all {items.length}</Button> : null}
                  </div>
                  {items.map((d, i) => (
                    <div key={`h-${d.id}`} style={{ display: "contents" }}>
                    {(i === 0 || items[i - 1].source_name !== d.source_name) && items.filter((x) => x.source_name === d.source_name).length > 1 && d.source_name ? (
                      <div className="ds-list__row ds-small" style={{ paddingLeft: 30, color: "var(--text-muted)" }}>
                        <FileSpreadsheet size={14} color="var(--success)" />
                        <span className="ds-grow ds-truncate">{d.source_name}</span>
                        <Button size="sm" variant="ghost" onClick={() => setPicked((p) => [...new Set([...p, ...items.filter((x) => x.source_name === d.source_name).map((x) => x.id)])])}>Tick file</Button>
                      </div>
                    ) : null}
                    <button key={d.id} type="button" className="ds-list__row is-clickable" onClick={() => setPicked((p) => (p.includes(d.id) ? p.filter((x) => x !== d.id) : [...p, d.id]))} aria-pressed={picked.includes(d.id)} style={{ width: "100%", border: 0, borderTop: "1px solid var(--divider)", background: picked.includes(d.id) ? "var(--accent-soft)" : "transparent", textAlign: "left", font: "inherit", color: "inherit" }}>
                      {picked.includes(d.id) ? <CheckSquare size={16} color="var(--accent)" /> : <Square size={16} className="ds-faint" />}
                      <Table2 size={16} className="ds-faint" />
                      <div className="ds-list__main">
                        <div className="ds-list__title ds-truncate">{d.name}</div>
                        <div className="ds-list__meta ds-truncate">{d.source_name ?? "—"}{d.description ? ` · ${d.description}` : ""}</div>
                      </div>
                      {picked.length === 0 ? <span className="ds-small" style={{ color: "var(--accent)", fontWeight: 600 }} onClick={(e) => { e.stopPropagation(); setPicked([d.id]); setDatasetId(d.id); setProposalId("executive"); }}>Suggest →</span> : picked.indexOf(d.id) === 0 ? <Badge tone="info">main</Badge> : null}
                    </button>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          )}
          {picked.length ? (
            <div className="ds-row" style={{ marginTop: 10, gap: 8, flexWrap: "wrap" }}>
              <span className="ds-small ds-muted">{picked.length === 1 ? "1 table picked." : `${picked.length} tables picked — the first gets the suggested dashboard, the others a page each, and they're linked so one filter works across all of them.`}</span>
              <div className="ds-grow" />
              <Button size="sm" onClick={() => setPicked([])}>Clear</Button>
              <Button size="sm" variant="primary" icon={Sparkles} onClick={() => { setDatasetId(picked[0]); setProposalId("executive"); }}>Suggest dashboards</Button>
            </div>
          ) : <div className="ds-small ds-faint" style={{ marginTop: 8 }}>Tick one or more tables — e.g. all the HR sheets for one HR dashboard.</div>}
        </div>
      ) : suggest.isLoading ? (
        <div>
          <div className="ds-small ds-muted" style={{ marginBottom: 12 }}>Reading your data and working out what each column is…</div>
          <div className="ds-grid" style={{ gridTemplateColumns: "repeat(3, minmax(0, 1fr))" }}>{[0, 1, 2].map((i) => <Skeleton key={i} height={300} />)}</div>
        </div>
      ) : suggest.isError ? (
        <Banner tone="danger" title="Couldn't suggest a dashboard">{suggest.error instanceof Error ? suggest.error.message : null}</Banner>
      ) : suggest.data ? (
        <div>
          <div className="ds-row" style={{ marginBottom: 12, flexWrap: "wrap", gap: 8 }}>
            <Badge tone="info">{profile?.datasetName}</Badge>
            <span className="ds-small ds-muted">
              {suggest.data.source.rowCount ? `${suggest.data.source.rowCount.toLocaleString()} rows` : "Rows couldn't be read — using the field settings"} · {profile?.fields.length} columns{kindsLine ? `: ${kindsLine}` : ""}
            </span>
          </div>
          {extraTables.length ? (
            <div style={{ marginBottom: 12 }}>
              <Banner tone="info" title={`Also adds ${extraTables.length} page${extraTables.length > 1 ? "s" : ""}: ${extraTables.map(tableName).join(", ")}`}>
                {links.isLoading ? "Looking for columns that link the tables…" : (links.data ?? []).length ? (
                  <span><Link2 size={13} style={{ verticalAlign: "-2px" }} /> Linked by {(links.data ?? []).map((l) => `${tableName(l.fromDatasetId)} ↔ ${tableName(l.toDatasetId)} (${l.why.replace(/^both have /, "").replace(/ · .*$/, "")})`).join("; ")}. You can change links later in the designer's Data tab.</span>
                ) : "No matching columns found to link them — you can link them yourself in the designer's Data tab."}
              </Banner>
            </div>
          ) : null}
          {suggest.data.source.status === "unavailable" && suggest.data.source.error ? <div style={{ marginBottom: 12 }}><Banner tone="warning" title="We couldn't read the rows just now">{suggest.data.source.error} The suggestions below are based on the column settings only.</Banner></div> : null}
          <div className="ds-grid" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))", marginBottom: 14 }}>
            {suggest.data.proposals.map((p) => {
              const selected = proposal?.id === p.id;
              return (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => setProposalId(p.id)}
                  className="ds-card ds-card--interactive"
                  aria-pressed={selected}
                  style={{ textAlign: "left", padding: 12, borderColor: selected ? "var(--accent)" : undefined, boxShadow: selected ? "var(--focus-ring)" : undefined, background: "var(--surface)", color: "var(--text)", display: "flex", flexDirection: "column", gap: 8 }}
                >
                  <LayoutThumb summary={summarizeDefinition(p.definition)} height={110} title={`${p.name} layout`} />
                  <div className="ds-row" style={{ justifyContent: "space-between" }}>
                    <span style={{ fontWeight: 700 }}>{selected ? <Check size={14} style={{ verticalAlign: "-2px", color: "var(--accent)" }} /> : null} {p.name}</span>
                    <DesignScoreBadge size="sm" score={p.designScore.score} grade={p.designScore.grade} />
                  </div>
                  <div className="ds-small ds-muted">{p.description}</div>
                  <ul style={{ margin: 0, paddingLeft: 16, fontSize: 12.5, lineHeight: 1.45, color: "var(--text)" }}>
                    {p.why.slice(0, 4).map((w) => <li key={w}>{w}</li>)}
                  </ul>
                  <div className="ds-small ds-faint">{describeVisuals(p.visuals.reduce<Record<string, number>>((a, v) => ({ ...a, [v.type]: (a[v.type] ?? 0) + 1 }), {}))}</div>
                  {p.measuresToCreate.length ? <div className="ds-small" style={{ color: "var(--text-muted)" }}>Adds {p.measuresToCreate.length} measure{p.measuresToCreate.length > 1 ? "s" : ""} to the dataset: {p.measuresToCreate.map((m) => m.display_name).join(", ")}.</div> : null}
                </button>
              );
            })}
          </div>
          {profile?.notes.length ? <div style={{ marginBottom: 12 }}><Banner tone="info" title="Tips about your data">{profile.notes.join(" ")}</Banner></div> : null}
        </div>
      ) : null}

      {path === "data" && datasetId && suggest.data ? nameFields : null}
      {error && !(path === "layout" || (datasetId && suggest.data)) ? <Banner tone="danger" title="Couldn't create the report">{error}</Banner> : null}
    </Modal>
  );
}

function TemplateChoice({ selected, onSelect, name, description, summary, badge }: { selected: boolean; onSelect: () => void; name: string; description: string; summary: ReturnType<typeof summarizeDefinition>; badge?: React.ReactNode }) {
  return (
    <button
      type="button"
      onClick={onSelect}
      className="ds-card ds-card--interactive"
      style={{ textAlign: "left", padding: 12, borderColor: selected ? "var(--accent)" : undefined, boxShadow: selected ? "var(--focus-ring)" : undefined, background: "var(--surface)", color: "var(--text)" }}
      aria-pressed={selected}
    >
      <LayoutThumb summary={summary} height={92} title={`${name} layout`} />
      <div className="ds-row" style={{ marginTop: 10, justifyContent: "space-between", gap: 6 }}>
        <span style={{ fontWeight: 650 }} className="ds-truncate">{name}</span>
        {badge}
      </div>
      <div className="ds-small ds-muted" style={{ marginTop: 2, minHeight: 34 }}>{description}</div>
      <div className="ds-small ds-faint" style={{ marginTop: 4 }}>{describeVisuals(summary.visualTypes)}{summary.pageCount > 1 ? ` · ${summary.pageCount} pages` : ""}</div>
    </button>
  );
}
